/**
 * Deterministic RNG. Same seed => identical sequence on every platform.
 * Used for ALL sim randomness (map gen, combat variance, bot decisions).
 * Critical for lockstep multiplayer determinism.
 */

/** xfnv1a hash -> 32-bit seed. */
function hashSeed(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 PRNG. */
export class RNG {
  private a: number;
  constructor(seed: number | string) {
    this.a = typeof seed === "string" ? hashSeed(seed) : seed >>> 0;
  }
  /** Internal state — serialize this to resume the exact same sequence (save/
   *  load + lockstep MP resync). */
  getState(): number {
    return this.a;
  }
  setState(a: number): void {
    this.a = a >>> 0;
  }
  /** float in [0, 1). */
  next(): number {
    // keep `a` a true uint32 — the mixing below only ever uses its low 32 bits,
    // so this changes no outputs, but it keeps the serialized RNG state a stable
    // 32-bit number (an unmasked `a` drifts past 2^32 and breaks save/load
    // round-trips, since setState re-masks it).
    this.a = (this.a + 0x6d2b79f5) >>> 0;
    let t = this.a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  /** integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
  /** pick a random element. */
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  /** gaussian-ish (sum of 3 uniforms), mean 0. */
  gauss(): number {
    return (this.next() + this.next() + this.next() - 1.5) / 1.5;
  }
}
